import type { TeactNode } from '../../../lib/teact/teact';
import React, {
  memo, useEffect, useLayoutEffect, useMemo, useRef, useState, useUnmountCleanup,
} from '../../../lib/teact/teact';
import { getActions, getGlobal, withGlobal } from '../../../global';

import type { ApiTonWalletVersion } from '../../../api/chains/ton/types';
import type { ApiBaseCurrency, ApiChain, ApiCurrencyRates, ApiGroupedWalletVariant } from '../../../api/types';
import type { Account, AccountChain, GlobalState, UserToken } from '../../../global/types';
import type Big from '../../../lib/big.js';

import { IS_CAPACITOR } from '../../../config';
import { dropEnclaveSessionHold, holdEnclaveSession } from '../../../global/helpers/enclave';
import {
  selectCurrentAccountId,
  selectCurrentAccountTokens,
  selectEnclaveToken,
  selectIsEnclaveSessionValid,
} from '../../../global/selectors';
import buildClassName from '../../../util/buildClassName';
import { calculateTokenPrice } from '../../../util/calculatePrice';
import { getOrderedAccountChains } from '../../../util/chain';
import { toBig, toDecimal } from '../../../util/decimals';
import { formatAccountAddresses } from '../../../util/formatAccountAddress';
import { formatCurrency, getShortCurrencySymbol } from '../../../util/formatNumber';
import resolveSlideTransitionName from '../../../util/resolveSlideTransitionName';
import { pause } from '../../../util/schedulers';
import { callApi } from '../../../api';

import useHistoryBack from '../../../hooks/useHistoryBack';
import useLang from '../../../hooks/useLang';
import useLastCallback from '../../../hooks/useLastCallback';
import useScrolledState from '../../../hooks/useScrolledState';

import Button from '../../ui/Button';
import MenuItem from '../../ui/MenuItem';
import PasswordForm from '../../ui/PasswordForm';
import Spinner from '../../ui/Spinner';
import Transition from '../../ui/Transition';
import SettingsHeader from '../SettingsHeader';

import styles from '../Settings.module.scss';

export interface Wallet {
  address: string;
  version: ApiTonWalletVersion;
  totalBalance: string;
  tokens: string[];
  isTestnetSubwalletId?: boolean;
}

interface OwnProps {
  isActive?: boolean;
  isInsideModal?: boolean;
  accountChains?: Account['byChain'];
  onBackClick: NoneToVoidFunction;
}

interface StateProps {
  accountId: string;
  tokens?: UserToken[];
  tokenInfo: GlobalState['tokenInfo'];
  currencyRates: ApiCurrencyRates;
  baseCurrency: ApiBaseCurrency;
}

type SubwalletGroup = {
  title: string;
  label?: string;
  addressContent?: TeactNode;
  assetAmounts: string;
  totalBalance: string;
};

const SEARCH_PAUSE = 1_000;
const MAX_EMPTY_RESULTS_IN_ROW = 5;

const enum SLIDES {
  password,
  walletVariants,
}

function SettingsWalletVariants({
  isActive,
  isInsideModal,
  accountChains,
  onBackClick,
  accountId,
  tokens,
  tokenInfo,
  currencyRates,
  baseCurrency,
}: OwnProps & StateProps) {
  const {
    closeSettings,
    addSubWallet,
    addAllFoundSubwallets,
    createSubWallet,
    releaseEnclaveSession,
  } = getActions();
  const lang = useLang();

  const [currentSlide, setCurrentSlide] = useState<SLIDES>(
    selectIsEnclaveSessionValid(getGlobal()) ? SLIDES.walletVariants : SLIDES.password,
  );

  const [enclaveToken, setEnclaveToken] = useState<string | undefined>(
    selectIsEnclaveSessionValid(getGlobal()) ? selectEnclaveToken(getGlobal()) : undefined,
  );
  const [groups, setGroups] = useState<ApiGroupedWalletVariant[]>([]);
  const [derivationsError, setDerivationsError] = useState<string>();
  const [isLoadingDerivations, setIsLoadingDerivations] = useState(true);

  /**
   * The screen reads the secret to list the variants and again to create the one that was picked,
   * so it owns its token for as long as it is open rather than for the length of one call. The hold
   * is what keeps the creation it started from losing the session when the screen closes behind it.
   */
  const heldTokenRef = useRef<string>();
  const isCreatingRef = useRef(false);

  const holdToken = useLastCallback((token: string) => {
    if (heldTokenRef.current === token) return;

    dropHeldToken();
    heldTokenRef.current = token;
    holdEnclaveSession(token);
  });

  const dropHeldToken = useLastCallback(() => {
    const token = heldTokenRef.current;
    if (!token) return;

    heldTokenRef.current = undefined;
    if (dropEnclaveSessionHold(token)) {
      releaseEnclaveSession({ enclaveToken: token });
    }
  });

  // The screen can also die without being deactivated first - switching the account rebuilds the
  // layout around it - and a hold nobody drops leaves a live read of the private key behind, with
  // no expiry to end it.
  useUnmountCleanup(dropHeldToken);

  const cleanup = useLastCallback(() => {
    dropHeldToken();
    isCreatingRef.current = false;
    setEnclaveToken(undefined);
    setGroups([]);
    setDerivationsError(undefined);
    setIsLoadingDerivations(true);
  });

  const handleBackToSettingsClick = useLastCallback(() => {
    cleanup();
    onBackClick();
  });

  useLayoutEffect(() => {
    if (!isActive) {
      cleanup();
      return;
    }

    const currentEnclaveToken = selectEnclaveToken(getGlobal());

    if (currentEnclaveToken && selectIsEnclaveSessionValid(getGlobal())) {
      holdToken(currentEnclaveToken);
      setEnclaveToken(currentEnclaveToken);
      setCurrentSlide(SLIDES.walletVariants);
    } else {
      setCurrentSlide(SLIDES.password);
    }
  }, [isActive]);

  useHistoryBack({
    isActive,
    onBack: handleBackToSettingsClick,
  });

  const {
    handleScroll,
    isScrolled,
    isAtEnd,
  } = useScrolledState();

  const handleGoToVariants = useLastCallback(() => {
    setCurrentSlide(SLIDES.walletVariants);
  });

  useEffect(() => {
    if (!isActive || !enclaveToken) return undefined;

    const currentAccountId = accountId;
    const currentEnclaveToken = enclaveToken;
    let isCancelled = false;

    setDerivationsError(undefined);
    setGroups([]);
    setIsLoadingDerivations(true);

    const runSearch = async () => {
      const mnemonicResult = await callApi('fetchMnemonic', currentAccountId, currentEnclaveToken);

      if (!mnemonicResult) {
        setDerivationsError('Unexpected error');
        return;
      }

      let page = 0;
      let emptyResultCounter = 0;
      const knownIndices = new Set<number>();

      try {
        while (!isCancelled) {
          const derivationsResult = await callApi(
            'getWalletVariants',
            currentAccountId,
            page,
            mnemonicResult,
          );

          if (!derivationsResult || 'error' in derivationsResult) {
            if (!isCancelled) {
              setDerivationsError(derivationsResult?.error ?? 'Unexpected error');
            }
            break;
          }

          if (isCancelled) break;

          const newItems = derivationsResult.filter((item) => !knownIndices.has(item.index));
          const hasPositiveBalance = derivationsResult.some((item) =>
            Object.values(item.byChain).some((e) => e
              && Object.values(e.balancesBySlug).some((balance) => balance > 0n),
            ));

          if (newItems.length > 0) {
            newItems.forEach((item) => knownIndices.add(item.index));
            setGroups((prev) => prev.concat(newItems));
          }

          emptyResultCounter = hasPositiveBalance ? 0 : emptyResultCounter + 1;
          page += 1;

          if (isCancelled || emptyResultCounter >= MAX_EMPTY_RESULTS_IN_ROW) break;

          await pause(SEARCH_PAUSE);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingDerivations(false);
        }
      }
    };

    void runSearch();

    return () => {
      isCancelled = true;
    };
  }, [accountId, enclaveToken, isActive]);

  const handleAuthorize = useLastCallback((authorizedEnclaveToken: string) => {
    holdToken(authorizedEnclaveToken);
    setEnclaveToken(authorizedEnclaveToken);
    handleGoToVariants();
  });

  const displayChains = useMemo(
    () => getOrderedAccountChains(accountChains ?? {}),
    [accountChains],
  );

  function renderSubwalletGroupContent(group: SubwalletGroup) {
    return (
      <>
        <div className={styles.walletVersionInfo}>
          <div className={styles.walletVariantLabelContainer}>
            <span className={styles.walletVersionTitle}>{group.title}</span>
            {group.label && (
              <span className={styles.walletVariantLabel}>{group.label}</span>
            )}
          </div>
          {Boolean(group.addressContent) && (
            <span className={styles.walletVersionAddress}>{group.addressContent}</span>
          )}
        </div>
        <div className={styles.walletVersionInfoRight}>
          <span className={styles.walletVersionTokens}>≥&thinsp;{group.totalBalance}</span>
          <span className={styles.walletVersionAmount} title={group.assetAmounts}>{group.assetAmounts}</span>
        </div>
      </>
    );
  }

  function getTokenMetaForSubwalletSlug(slug: string) {
    const info = tokenInfo?.bySlug[slug];
    if (!info) return undefined;

    const price = calculateTokenPrice(info.priceUsd ?? 0, baseCurrency, currencyRates);

    return {
      slug: info.slug,
      decimals: info.decimals,
      priceUsd: info.priceUsd ?? 0,
      symbol: info.symbol,
      price,
    };
  }

  function buildSubwalletRowModel(
    dotIndex: number,
    label: string | undefined,
    orderedChains: ApiChain[],
    getBalancesForChain: (chain: ApiChain) => Record<string, bigint> | undefined,
    getAccountChainForRow: (chain: ApiChain) => AccountChain | undefined,
  ): SubwalletGroup {
    const shortBaseSymbol = getShortCurrencySymbol(baseCurrency);

    const assetItems: { fiat: Big; part: string }[] = [];
    let fiatAccum = toBig(0);
    const walletsByChain: Account['byChain'] = {};

    for (const chain of orderedChains) {
      const accountChain = getAccountChainForRow(chain);
      if (!accountChain) continue;

      walletsByChain[chain] = accountChain;

      const balancesBySlug = getBalancesForChain(chain) ?? {};

      for (const [slug, balance] of Object.entries(balancesBySlug)) {
        const meta = getTokenMetaForSubwalletSlug(slug);

        if (meta && balance > 0n) {
          const rowFiat = toBig(balance, meta.decimals).mul(meta.price);

          assetItems.push({
            fiat: rowFiat,
            part: formatCurrency(toDecimal(balance, meta.decimals), meta.symbol),
          });

          fiatAccum = fiatAccum.add(rowFiat);
        }
      }
    }

    const assetAmounts = [...assetItems].sort((a, b) => {
      if (a.fiat.gt(b.fiat)) return -1;
      if (a.fiat.lt(b.fiat)) return 1;
      return 0;
    }).map(({ part }) => part).join(', ');

    return {
      title: `.${dotIndex + 1}`,
      label,
      addressContent: formatAccountAddresses(walletsByChain, getOrderedAccountChains(walletsByChain), 'small'),
      assetAmounts,
      totalBalance: formatCurrency(fiatAccum, shortBaseSymbol),
    };
  }

  function buildSubwalletGroup(group: ApiGroupedWalletVariant): SubwalletGroup {
    const orderedChains = getOrderedAccountChains(group.byChain);
    let label: string | undefined;
    for (const chain of orderedChains) {
      const derivation = group.byChain[chain]?.wallet.derivation;
      if (derivation) {
        label = derivation.label;
        break;
      }
    }

    return buildSubwalletRowModel(
      group.index,
      label,
      orderedChains,
      (chain) => group.byChain[chain]?.balancesBySlug,
      (chain) => {
        const entry = group.byChain[chain];
        if (!entry) return undefined;

        const accountChain: AccountChain = { address: entry.wallet.address };
        if (entry.wallet.derivation) {
          accountChain.derivation = entry.wallet.derivation;
        }

        return accountChain;
      },
    );
  }

  const currentSubwalletRowModel = useMemo((): SubwalletGroup => {
    let rowIndex = 0;
    let label: string | undefined;
    for (const chain of displayChains) {
      const derivation = accountChains?.[chain]?.derivation;
      if (typeof derivation?.index === 'number') {
        rowIndex = derivation.index;
        label = derivation.label;
        break;
      }
    }

    const accountTokens = tokens ?? [];

    return buildSubwalletRowModel(
      rowIndex,
      label,
      displayChains,
      (chain) => {
        const bySlug: Record<string, bigint> = {};
        for (const t of accountTokens) {
          if (t.chain === chain && t.amount > 0n) {
            bySlug[t.slug] = t.amount;
          }
        }
        return bySlug;
      },
      (chain) => accountChains?.[chain],
    );
    // buildSubwalletRowModel is redefined each render. tokenInfo / rates / currency trigger rerender only if needed.
    // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, [displayChains, accountChains, tokens, tokenInfo, baseCurrency, currencyRates]);

  const handleCreateSubwallet = useLastCallback(() => {
    const currentEnclaveToken = enclaveToken ?? selectEnclaveToken(getGlobal());
    // The button outlives the click by a render, and the budget covers a single creation: a second
    // one would ask for a read the session no longer has and report a correct password as wrong.
    if (!currentEnclaveToken || isCreatingRef.current) return;

    isCreatingRef.current = true;
    createSubWallet({ enclaveToken: currentEnclaveToken });
    closeSettings();
  });

  const handleGroupClick = useLastCallback((
    _e: React.SyntheticEvent<HTMLDivElement | HTMLAnchorElement>,
    group: ApiGroupedWalletVariant,
  ) => {
    addSubWallet({ group });
    closeSettings();
  });

  const handleAddAllFoundSubwallets = useLastCallback(() => {
    if (!groups.length) return;

    addAllFoundSubwallets({ foundSubwallets: groups });
    closeSettings();
  });

  const hasWallets = groups.length > 0;

  function renderCurrentWalletBlock() {
    return (
      <>
        <p className={buildClassName(styles.blockTitle, styles.blockTitle_small)}>{lang('Current Wallet')}</p>
        <div className={styles.settingsBlock}>
          <div
            className={buildClassName(styles.item, styles.item_wallet_no_arrow, styles.item_nonInteractive)}
          >
            {renderSubwalletGroupContent(currentSubwalletRowModel)}
          </div>
        </div>
      </>
    );
  }

  function renderSubwalletList() {
    return (
      <div className={buildClassName(styles.block, styles.settingsBlockWithDescription)}>
        {groups.map((group) => {
          const rowModel = buildSubwalletGroup(group);

          return (
            <MenuItem<ApiGroupedWalletVariant>
              key={group.index}
              ignoreBaseClassName
              className={buildClassName(styles.item, styles.item_wallet_no_arrow)}
              onClick={handleGroupClick}
              clickArg={group}
            >
              {renderSubwalletGroupContent(rowModel)}
            </MenuItem>
          );
        })}
      </div>
    );
  }

  function renderSubwalletsSection() {
    if (derivationsError) {
      return (
        <div className={styles.emptyList}>
          <span className={styles.emptyListTitle}>{lang(derivationsError)}</span>
        </div>
      );
    }

    return (
      <>
        <div className={styles.blockTitleRow}>
          <p className={buildClassName(styles.blockTitle, styles.blockTitle_small)}>{lang('Subwallets')}</p>
          {isLoadingDerivations ? (
            <span className={styles.scanningLabel}>
              <Spinner className={styles.scanningSpinner} />
              {lang('Scanning...')}
            </span>
          ) : hasWallets && (
            <span className={styles.scanningLabel}>
              {lang('$subwallets_found', groups.length, 'i')}
            </span>
          )}
        </div>
        {hasWallets
          ? renderSubwalletList()
          : !isLoadingDerivations && (
            <div className={styles.emptySubwallets}>
              {lang('No subwallets yet.')}
            </div>
          )}
      </>
    );
  }

  function renderUnifiedContent() {
    return (
      <div className={styles.slide}>
        <SettingsHeader
          title={lang('Subwallets')}
          isScrolled={isScrolled}
          onBackClick={handleBackToSettingsClick}
        />

        <div className={styles.contentWrapper}>
          <div
            className={buildClassName(styles.content, styles.contentWallets, 'custom-scroll')}
            onScroll={handleScroll}
          >
            <div className={styles.blockDescription}>
              {lang('Use subwallets to get additional addresses without creating new secret words.')}
            </div>

            {renderCurrentWalletBlock()}

            <div className={buildClassName(styles.blockDescription, styles.noTopMargin)}>
              {lang('If you have previously created subwallets, they will appear in the list below.')}
            </div>

            {renderSubwalletsSection()}

            {hasWallets && groups.length > 1 && (
              <div className={styles.addAllFoundSubwalletsContainer}>
                <Button
                  isSecondary
                  className={styles.addAllFoundButton}
                  onClick={handleAddAllFoundSubwallets}
                >
                  {lang('Add All Found')}
                </Button>
              </div>
            )}
          </div>

          <div className={buildClassName(
            styles.createSubwalletContainer,
            !isAtEnd && styles.createSubwalletWithBorder,
          )}
          >
            <Button
              isPrimary
              onClick={handleCreateSubwallet}
              className={styles.createSubwalletButton}
            >
              <i className={buildClassName('icon-plus', styles.createSubwalletIcon)} aria-hidden />
              {lang('Create Subwallet')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  function renderContent(isSlideActive: boolean, _isFrom: boolean, _currentKey: number) {
    switch (currentSlide) {
      case SLIDES.password:
        return (
          <div className={styles.slide}>
            <SettingsHeader
              title={lang('Confirm Password')}
              onBackClick={handleBackToSettingsClick}
            />
            <PasswordForm
              isActive={isSlideActive && !!isActive}
              containerClassName={IS_CAPACITOR ? styles.passwordFormContent : styles.passwordFormWithHeaderOffset}
              placeholder={lang('Enter your current password')}
              forceBiometricsInMain={!isInsideModal}
              submitLabel={lang('Continue')}
              noAutoConfirm
              // Listing the variants reads the secret, and creating the one that was picked reads
              // it again
              extraAuthUsages={1}
              onCancel={handleBackToSettingsClick}
              onAuthorize={handleAuthorize}
            />
          </div>
        );

      case SLIDES.walletVariants:
        return renderUnifiedContent();
    }
  }

  return (
    <Transition
      name={resolveSlideTransitionName()}
      className={buildClassName(styles.transitionContainer, 'custom-scroll')}
      activeKey={currentSlide}
      withSwipeControl
    >
      {renderContent}
    </Transition>
  );
}

export default memo(withGlobal<OwnProps>((global): StateProps => {
  const currentAccountId = selectCurrentAccountId(global)!;
  return {
    accountId: currentAccountId,
    tokens: selectCurrentAccountTokens(global),
    tokenInfo: global.tokenInfo,
    currencyRates: global.currencyRates,
    baseCurrency: global.settings.baseCurrency,
  };
})(SettingsWalletVariants));
